import { useQuery } from '@tanstack/react-query'
import type { Phrasebook } from '@shared/types/phrasebook'
import { api } from './api'

const CACHE_KEY = 'pj.phrasebook.v1'

/**
 * The phrasebook, cached in localStorage.
 *
 * The last successful response is kept and served immediately on the next
 * open, so the screen paints from disk instead of waiting on the network.
 * React Query revalidates in the background.
 *
 * Note this is *not* offline support: without a service worker the app shell
 * cannot boot without a connection, so this cache is only ever reached when
 * the network is already there. It makes repeat opens instant, nothing more.
 */
function readCache(): Phrasebook | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as Phrasebook) : undefined
  } catch {
    return undefined
  }
}

function writeCache(data: Phrasebook): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data))
  } catch {
    // A full quota costs the offline copy, not the screen.
  }
}

export function usePhrasebook() {
  return useQuery({
    queryKey: ['phrasebook'],
    queryFn: async () => {
      const data = await api<Phrasebook>('/phrasebook')
      writeCache(data)
      return data
    },
    // Serving the cached copy as initial data is what makes this work with no
    // signal: the screen renders from disk, then quietly updates if it can.
    initialData: readCache,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}
